import React, { FC, useMemo } from "react";
import { Checkbox, IconButton, Typography, TableHead, TableBody, Tooltip } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { GkvEditBillTableProps } from "../types/interface";
import RubberStamp from "../../common/components/RubberStamp";
import {
  DenseTableContainer,
  DenseTable,
  HeaderCell,
  DataCell,
  DenseTableRow,
  RxDetailsRow,
  TherapyTag,
} from "../../GKVNew/styles/primitives";
import { table } from "../../GKVNew/styles/tokens";
import { SummarizedValidityIndicator } from "../../../../Base/components/SummarizedValidityIndicator";

/**
 * Table displaying Rx rows within a bill card.
 * Design matches GKVNew GkvBillTable:
 * - Columns: Checkbox | Name | Rx Details (number + date + therapies) | Taxieren | Actions
 * - Row click → detail panel
 * - Checkbox for bulk selection
 * - RubberStamp for taxieren status
 */
const GkvEditBillTable: FC<GkvEditBillTableProps> = ({
  rxs,
  selectedRxId,
  selectedRxIds,
  validationMap,
  onRowClick,
  onMenuOpen,
  onTaxieren,
  onToggleRxSelection,
}) => {
  /** Sort Rxs by patient lastName → rxNumber */
  const sortedRxs = useMemo(
    () =>
      [...rxs].sort((a, b) => {
        const lastA = (a.patient?.lastName || "").toLowerCase();
        const lastB = (b.patient?.lastName || "").toLowerCase();
        if (lastA !== lastB) return lastA.localeCompare(lastB);
        return (a.rxNumber || "").localeCompare(b.rxNumber || "");
      }),
    [rxs],
  );

  return (
    <DenseTableContainer>
      <DenseTable stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <DenseTableRow>
            <HeaderCell padding="checkbox" sx={{ width: "42px" }} />
            <HeaderCell sx={{ width: "22%" }}>Name</HeaderCell>
            <HeaderCell sx={{ width: "35%" }}>Rx Details</HeaderCell>
            <HeaderCell sx={{ width: "15%" }}>Validierung</HeaderCell>
            <HeaderCell sx={{ width: "18%" }}>Taxieren</HeaderCell>
            <HeaderCell sx={{ width: "48px" }} />
          </DenseTableRow>
        </TableHead>
        <TableBody>
          {sortedRxs.map((rx) => {
            const isSelected = rx.id === selectedRxId;

            return (
              <DenseTableRow key={rx.id} isSelected={isSelected} onClick={() => onRowClick(rx)}>
                {/* Checkbox */}
                <DataCell padding="checkbox" sx={{ width: table.checkboxWidth }}>
                  <Checkbox
                    size="small"
                    checked={selectedRxIds.has(rx.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleRxSelection(rx.id)}
                  />
                </DataCell>

                {/* Patient name */}
                <DataCell>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: table.cellFontSize, fontWeight: 500 }}
                  >
                    {rx.patient ? `${rx.patient.lastName}, ${rx.patient.firstName}` : "–"}
                  </Typography>
                </DataCell>

                {/* Rx number + date + therapies */}
                <DataCell>
                  <RxDetailsRow>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: table.cellFontSize, fontWeight: 600, minWidth: "70px" }}
                    >
                      {rx.rxNumber}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: table.cellFontSize,
                        color: "text.secondary",
                        minWidth: "80px",
                      }}
                    >
                      {rx.date
                        ? new Date(rx.date).toLocaleDateString("de-DE", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                        : "–"}
                    </Typography>
                    <span>
                      {rx.therapies?.map((t, i) => (
                        <TherapyTag key={i}>{t.abbreviation}</TherapyTag>
                      ))}
                    </span>
                  </RxDetailsRow>
                </DataCell>

                {/* Validation status */}
                <DataCell align="left">
                  {SummarizedValidityIndicator(validationMap[rx.id])}
                </DataCell>

                {/* Taxieren stamp — click to taxieren */}
                <DataCell align="left">
                  <Tooltip title={rx.isTaxieren ? "Taxiert" : "Klicken zum Taxieren"}>
                    <span>
                      <RubberStamp
                        text={rx.isTaxieren ? "taxiert" : "Taxieren"}
                        color={rx.isTaxieren ? "success" : "info"}
                        isdisabled={false}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!rx.isTaxieren) {
                            onTaxieren(rx);
                          }
                        }}
                      />
                    </span>
                  </Tooltip>
                </DataCell>

                {/* Actions menu */}
                <DataCell align="center" sx={{ width: "48px" }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuOpen(e, rx);
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </DataCell>
              </DenseTableRow>
            );
          })}
        </TableBody>
      </DenseTable>
    </DenseTableContainer>
  );
};

export default React.memo(GkvEditBillTable);
